import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  model,
  output,
  signal,
  TemplateRef,
  viewChild,
} from "@angular/core";
import { ChipsComponent } from "../chips/chips.component";
import { InputSearchComponent } from "../input-search/input-search.component";
import { NgClass, NgTemplateOutlet } from "@angular/common";
import { ControlValueAccessor, NG_VALIDATORS, NgControl } from "@angular/forms";
import { MatMenuModule, MatMenuTrigger } from "@angular/material/menu";
import { MatLabel } from "@angular/material/form-field";
import { asapScheduler } from "rxjs";
import { SnackService } from "../../services/snack-service.service";
import { UserInfoCardComponent } from "../../components/user-info-card/user-info-card.component";
import { AideContextComponent } from "../../components/aide-context/aide-context.component";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { SignalUtils } from "../../services/signal-utils.service";
import { SharedModule } from "../../shared.module";
import { MatPseudoControlDirective } from "../../directives/pseudo-controle.directive";
import { VlAideContentDirective } from "../../directives/aide-content.directive";

@Component({
  selector: "vl-select-autocomplete",
  standalone: true,
  imports: [
    ChipsComponent,
    InputSearchComponent,
    MatMenuModule,
    MatLabel,
    UserInfoCardComponent,
    AideContextComponent,
    NgTemplateOutlet,
    NgClass,
    SharedModule,
    MatPseudoControlDirective,
    VlAideContentDirective,
  ],
  templateUrl: "./select-autocomplete.component.html",
  styleUrl: "./select-autocomplete.component.scss",
})
export class SelectAutocompleteComponent<T = string>
  implements ControlValueAccessor
{
  menuTrigger = viewChild<MatMenuTrigger>("menuTrigger");
  searchInput = viewChild(InputSearchComponent);
  ngControl = inject(NgControl, { optional: true, self: true });
  validators = inject(NG_VALIDATORS, { optional: true, self: true });
  statusChange = signal(null);
  destroyRef = inject(DestroyRef);
  template = input<TemplateRef<any>>();
  required = input<boolean>(false);
  controlStatus = signal(null);
  optionMenu = input<TemplateRef<any>>();
  minWidth = input(true);
  showSubscript = input(true);
  loading = input(false);

  isRequired = computed(() => {
    if (this.required()) {
      return true;
    }
    this.statusChange();
    this.value();
    if (this.ngControl) {
      return !!this.ngControl.control?.validator?.({
        value: null,
      } as any)?.required;
    }
    return false;
  });

  constructor() {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
    effect(() => {
      const value = this.value();
      if (value) {
        this.menuTrigger()?.closeMenu();
      }
    });
    effect(() => {
      if (
        this.menuTrigger() &&
        !this.menuTrigger().menuOpen &&
        this.popoverOnly()
      ) {
        this.menuTrigger().openMenu();
      }
    });
    effect(() => {
      const trigger = this.menuTrigger();
      // En mode démo (tutoriel), ne pas auto-ouvrir : le menu s'ouvre avant le
      // layout du dialog → overlay 0×0 inerte, et l'aide à la saisie du tuto
      // ne peut plus sélectionner d'option.
      const demoMode = localStorage.getItem('tutorial-demo-mode') === 'true';
      if (this.autoOpen() && !demoMode && trigger && !trigger.menuOpen) {
        setTimeout(() => trigger.openMenu(), 200);
      }
    });
  }

  ngOnInit() {
    if (this.ngControl) {
      this.ngControl.statusChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((status) => {
          this.statusChange.set(status);
        });
    }
  }

  ngAfterViewInit() {
    if (this.ngControl?.control) {
      this.controlStatus.set(SignalUtils.formStatus(this.ngControl.control));
    }
  }

  onChangeFn = (v) => {};
  onTouchFn = (v) => {};

  snackService = inject(SnackService);

  selectLabel = input<string>("Sélectionner");
  header = input<boolean>(false);
  footer = input(false);
  sideSearch = input<boolean>(false);
  /** Active une slot `.subsearch-autocomplete` projetée juste sous la
   *  barre de recherche, avant la liste d'options. Utile pour des
   *  toggles/chips de filtre additionnels ("inclure inactifs", "afficher
   *  fabriqués"…) qui doivent rester visibles pendant que l'utilisateur
   *  saisit/scroll. */
  subSearch = input<boolean>(false);
  helperKey = input<string>();
  label = input<string>();
  color = input<
    | "primary"
    | "basic"
    | "warn"
    | "accent"
    | "secondary"
    | "canvas"
    | "enabled"
    | "active"
  >("enabled");
  mode = input<"select" | "add">("select");
  canCreate = input<boolean>(false);
  type = input<"default" | "userInfos" | "template">("default");
  templateButton = input<boolean>(true);
  icon = input<string>(null);
  addLabel = input("Sélectionner");
  popoverOnly = input(false);
  autoOpen = input(false);
  disabled = input<boolean>(false);
  filterInactive = input<boolean>(false);
  displayOnFilter = input<boolean>(false);
  emptyPlaceholder = input<string>();
  maxHeight = input<string>("200px");
  options = input<
    {
      name: string;
      subname?: string;
      title?: string;
      id?: string;
      disabled?: boolean;
      value: T;
    }[]
  >();
  suggestions = input<
    {
      name: string;
      subname?: string;
      title?: string;
      id?: string;
      value: T;
    }[]
  >();
  acceptableOptions = input<
    {
      name: string;
      subname?: string;
      title?: string;
      id?: string;
      value: T;
    }[]
  >();
  acceptableOptionsError = input<string>();
  isEqualPredicate = input<(a: T, b: T) => boolean>(
    (a, b) =>
      a === b ||
      ((a as any)?.id && (b as any)?.id && (a as any).id === (b as any).id),
  );
  buildPredicate = input<(value: string) => T>((v) => v as T);
  filterPredicate = input<
    (
      e: {
        name: string;
        subname?: string;
        title?: string;
        id?: string;
        value: T;
      },
      string,
    ) => boolean
  >((o, search) =>
    search
      .trim()
      .toLowerCase()
      .split(" ")
      .every((w) =>
        `${o.name} ${o.subname}${o.title}`.trim().toLowerCase().includes(w),
      ),
  );

  onAdd = output<T>();
  onCreate = output();
  formDisabled = signal(false);
  value = model<T>();

  buttonColor = computed(() => {
    const status = this.statusChange();
    const value = this.value();
    const controlState = this.controlStatus()?.touched?.();
    if (this.isRequiredError()) {
      return "warn";
    }
    if (value) {
      return "active";
    } else {
      return this.color();
    }
  });

  isRequiredError = computed(() => {
    const value = this.value();
    const status = this.statusChange();
    const controlState = this.controlStatus()?.touched?.();
    return this.ngControl?.errors && this.ngControl.touched;
  });

  name = computed(() => {
    const v = this.value();
    const options = this.options();
    const isEqualPredicate = this.isEqualPredicate();
    if (v) {
      const option = options?.find((o) => isEqualPredicate(o.value, v));
      if (option) return option.name;
      if (typeof v === "string") return v;
      return (v as any)?.denomination ?? (v as any)?.name ?? "";
    }
  });

  subname = computed(() => {
    const v = this.value();
    const options = this.options();
    const isEqualPredicate = this.isEqualPredicate();
    if (v) {
      const option = options?.find((o) => isEqualPredicate(o.value, v));
      return option ? option.subname : undefined;
    }
  });

  selectedOption = computed(() => {
    const v = this.value();
    const options = this.options();
    const isEqualPredicate = this.isEqualPredicate();
    if (v && options) {
      return options.find((o) => isEqualPredicate(o.value, v)) ?? null;
    }
    return null;
  });

  isDisabled = computed(() => {
    return this.formDisabled() || this.disabled();
  });

  setDisabledState(isDisabled: boolean) {
    asapScheduler.schedule(() => {
      this.formDisabled.set(isDisabled);
    });
  }

  searchValue = model<string>();
  optionsFiltered = computed(() => {
    const search = this.searchValue();
    const filterInactive = this.filterInactive();

    const isActiveFilter = (o: { value: T }) =>
      !filterInactive || (o.value as any)?.isActive !== false;

    if (!search) {
      if (this.displayOnFilter()) {
        return [];
      }
      return this.options()
        ?.filter(
          (o) =>
            !o.disabled &&
            isActiveFilter(o) &&
            (this.mode() === "add" ||
              !this.value() ||
              !this.isEqualPredicate()(o.value, this.value())),
        )
        .slice(0, 50);
    }
    return (
      this.options()?.filter(
        (o) =>
          !o.disabled &&
          isActiveFilter(o) &&
          this.filterPredicate()(o, this.searchValue()) &&
          o.value !== this.name() &&
          !this.isEqualPredicate()(o.value, this.value()),
      ) ?? []
    ).slice(0, 50);
  });
  buttonIcon = computed(() => {
    //return this.mode() === "add" ? "add" : this.icon();
    return this.icon();
  });

  buttonText = computed(() => {
    if (this.mode() === "select") {
      if (this.name()) {
        return this.name();
      } else {
        return this.disabled() || this.formDisabled()
          ? "Aucun"
          : this.selectLabel();
      }
    } else if (this.mode() === "add") {
      return this.addLabel();
    }
  });
  buttonIconRight = computed(() => {
    //if (this.mode() === "select") {
    //  return this.isDisabled() ? null : "expand_more";
    //} else if (this.mode() === "add") {
    //  return null;
    //}
    return this.isDisabled() ? null : "expand_more";
  });

  /**
   * Clé string stable pour le `data-testid` d'une option (testabilité e2e +
   * accessibilité). `value` peut être un objet entité → on déréférence son id,
   * sinon on retombe sur l'id de l'option ou son nom.
   */
  optKey(option: { name?: string; value?: unknown; id?: unknown }): string {
    const v = option?.value as { id?: unknown } | string | number | null;
    if (typeof v === "string" || typeof v === "number") return String(v);
    if (v && typeof v === "object" && "id" in v && v.id != null)
      return String(v.id);
    if (option?.id != null) return String(option.id);
    return (option?.name ?? "").toString().trim().replace(/\s+/g, "-") || "x";
  }

  selectOption(option: { name: string; value: T }) {
    const isEqualPredicate = this.isEqualPredicate();
    if (this.acceptableOptions()?.length) {
      if (
        !this.acceptableOptions().find((o) =>
          isEqualPredicate(o.value, option.value),
        )
      ) {
        this.snackService.error(this.acceptableOptionsError());
        return;
      }
    }

    if (this.mode() === "add") {
      this.onAdd.emit(option.value);
      return;
    }
    this.value.set(option.value);
    this.onChangeFn(this.value());
    this.searchValue.set("");
    this.onTouchFn(true);
  }

  unSelect() {
    this.value.set(null);
    this.onTouchFn(true);
    this.onChangeFn(this.value());
  }

  writeValue(value: T): void {
    asapScheduler.schedule(() => {
      this.value.set(value);
    });
  }

  registerOnChange(fn: any): void {
    this.onChangeFn = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchFn = fn;
  }

  openMenu(menuTrigger: MatMenuTrigger) {
    this.onTouchFn(true);
    menuTrigger.openMenu();
  }

  onMenuOpened() {
    setTimeout(() => this.searchInput()?.focus(), 50);
  }

  menuTriggerOptions = viewChild<MatMenuTrigger>("menuTriggerOption");

  closeMenuOption(e: MouseEvent) {
    if (this.menuTriggerOptions()) {
      this.menuTriggerOptions().closeMenu();
    }
    e.stopPropagation();
  }
}
